import { useEffect, useState } from 'react';
import * as githubClient from './api/github-backup-client';
import type { GithubBackupState, GithubTestResult, PendingItem } from './api/github-backup-client';
import styles from './tenant-settings.module.css';

export function GithubBackupSection() {
  const [state, setState] = useState<GithubBackupState | null>(null);
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<GithubTestResult | null>(null);
  const [pending, setPending] = useState<PendingItem[]>([]);

  async function loadAll() {
    const [cfg, pend] = await Promise.all([githubClient.get(), githubClient.pending()]);
    setState(cfg);
    setPending(pend.items);
  }

  useEffect(() => {
    void loadAll();
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!state) return;
    setSaving(true);
    try {
      await githubClient.put({
        enabled: state.enabled,
        owner: state.owner,
        repo: state.repo,
        branch: state.branch,
        ...(token ? { token } : {}),
      });
      setToken('');
      await loadAll();
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await githubClient.test();
      setTestResult(result);
    } finally {
      setTesting(false);
    }
  }

  // The parent <section className={styles.section}><h2>Configuration backup</h2>
  // already labels this surface, so we render only the form to avoid the
  // doubled-heading IA noise (#81).
  if (!state) {
    return <p>Loading…</p>;
  }

  return (
    <>
      <form onSubmit={handleSave} className={styles.backupForm}>
        <div className={styles.formRow}>
          <label htmlFor="github-enabled" className={styles.checkboxLabel}>
            <input
              id="github-enabled"
              type="checkbox"
              checked={state.enabled}
              onChange={(e) => setState({ ...state, enabled: e.target.checked })}
            />
            Enable GitHub backup
          </label>
        </div>

        <div className={styles.formGrid}>
          <label htmlFor="github-owner" className={styles.fieldLabel}>
            Owner
          </label>
          <input
            id="github-owner"
            type="text"
            value={state.owner}
            onChange={(e) => setState({ ...state, owner: e.target.value })}
            className={styles.textInput}
          />

          <label htmlFor="github-repo" className={styles.fieldLabel}>
            Repository
          </label>
          <input
            id="github-repo"
            type="text"
            value={state.repo}
            onChange={(e) => setState({ ...state, repo: e.target.value })}
            className={styles.textInput}
          />

          <label htmlFor="github-branch" className={styles.fieldLabel}>
            Branch
          </label>
          <input
            id="github-branch"
            type="text"
            value={state.branch}
            onChange={(e) => setState({ ...state, branch: e.target.value })}
            className={styles.textInput}
          />

          <label htmlFor="github-token" className={styles.fieldLabel}>
            Personal Access Token
          </label>
          <input
            id="github-token"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={state.hasToken ? '(unchanged — leave blank to keep existing)' : 'Paste GitHub PAT'}
            className={styles.textInput}
          />
        </div>

        <div className={styles.actionRow}>
          <button type="submit" disabled={saving} className={styles.primaryButton}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" disabled={testing} onClick={handleTest} className={styles.secondaryButton}>
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
          {testResult && (
            <span className={testResult.ok ? styles.testOk : styles.testErr}>
              {testResult.ok ? `✓ ok${testResult.sha ? ` (sha ${testResult.sha})` : ''}` : `✗ ${testResult.error ?? 'error'}`}
            </span>
          )}
        </div>
      </form>

      <div className={styles.pendingSection}>
        <h3 className={styles.subHeading}>Pending Retries</h3>
        {pending.length === 0 ? (
          <p className={styles.emptyState}>No pending retries.</p>
        ) : (
          <table className={styles.pendingTable}>
            <thead>
              <tr>
                <th>File</th>
                <th>Attempts</th>
                <th>Last Error</th>
                <th>Next Retry</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((item) => (
                <tr key={item.file}>
                  <td>{item.file}</td>
                  <td>{item.attempts}</td>
                  <td>{item.lastError ?? '—'}</td>
                  <td>{item.nextRetry ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
